import { Heading, InfoIcon, Modal, Notification, Select, style, Tooltip, TSelectOption } from "@r360/library";
import React, { useEffect, useRef, useState } from "react";
import Tesseract from "tesseract.js";
import AxiosClient from "../../API/AxiosClient";
import useAppSelector from "../../hooks/useAppSelector";
import useTranslate from "../../hooks/useTranslate";
import { optionsSelector } from "../../selectors/Selectors";
import { RichText } from "../UI";
import { TWtpVideoStreamHandle, WtpVideoStream } from "./WtpVideoStream";

type TWtpScannerModal = {
    show: boolean;
    onCloseModal: () => void;
    onMatch: (arg0: string) => void;
    selectedDeviceId?: string;
    onDeviceChange: (arg0: string) => void;
};

export const WtpScannerModal = ({
    show,
    onCloseModal,
    onMatch,
    selectedDeviceId,
    onDeviceChange,
}: TWtpScannerModal) => {
    const t = useTranslate();
    const useAdvancedOCR = useAppSelector(optionsSelector).customer.advanced_ocr;
    const { isMobile, isTablet, isDesktop, isIframe, iframeOffsetTop } = useAppSelector(state => state.window);
    const videoStreamRef = useRef<TWtpVideoStreamHandle | null>(null);
    const [azureVisionLoading, setAzureVisionLoading] = useState(false);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [preferredDevice, setPreferredDevice] = useState<MediaDeviceInfo | undefined>(undefined);

    useEffect(() => {
        setAzureVisionLoading(false);
    }, [show]);

    const getSkipassNumber = (inputString: string) => {
        const skidataId = extractSkidataId(inputString);
        const teamAxessId = extractTeamAxessId(inputString);

        if (skidataId) {
            // Remove "-" and whitespace.
            return skidataId.replaceAll("-", "").replaceAll(" ", "");
        }

        if (teamAxessId) {
            // Remove "-" and whitespace.
            return teamAxessId.replaceAll("-", "").replaceAll(" ", "");
        }

        return undefined;
    };

    const extractSkidataId = (input: string) => {
        const skidataPattern = /(\d{2}-\d{4} \d{4} \d{4} \d{4} \d{4}-\d)/; // 12-3456 7890 1234 5678 9012-3
        const skidataMatch = input.match(skidataPattern);
        return skidataMatch ? skidataMatch[0] : null;
    };

    const extractTeamAxessId = (input: string) => {
        const teamAxessPattern = /[a-zA-Z0-9]{8}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}/i; // 12345678-abc-DEF
        const teamAxessMatch = input.match(teamAxessPattern);
        return teamAxessMatch ? teamAxessMatch[0] : null;
    };

    const performOCR = async (image: string) => {
        // const worker = await createWorker("eng");

        // await worker.setParameters({
        //     tessedit_char_whitelist: "-0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
        //     tessedit_ocr_engine_mode: 1,
        //     // PSM.SINGLE_LINE is used to improve the accuracy. Telling Tesseract to focus on a single line.
        //     tessedit_pageseg_mode: PSM.SINGLE_LINE,
        // });

        try {
            const {
                data: { text },
            } = await Tesseract.recognize(image, "eng");

            const extractedId = getSkipassNumber(text);

            if (extractedId) {
                if (useAdvancedOCR) {
                    const extractedIdFromAzureVision = await performOCRWithAzureVision(image);

                    if (extractedIdFromAzureVision) {
                        return extractedIdFromAzureVision;
                    }
                }

                return extractedId;
            } else {
                return null;
            }
        } catch (error) {
            console.error("Error during OCR:", error);
        }
    };

    const performOCRWithAzureVision = async (image: string): Promise<string> => {
        try {
            // Fetch image and convert to blob.
            const response = await fetch(image);
            const imageBlob = await response.blob();

            const formData = new FormData();
            formData.append("skipass", imageBlob);

            videoStreamRef.current?.pauseVideo();
            setAzureVisionLoading(true);
            const azureResponse = await AxiosClient.post("/ai/skipass_ocr", formData);

            const formattedResponse = azureResponse.data.payload.replaceAll("-", "").replaceAll(" ", "");
            return formattedResponse;
        } catch (error) {
            console.error("Error during Azure OCR:", error);
            videoStreamRef.current?.unPauseVideo();
            setAzureVisionLoading(false);
            return "";
        }
    };

    const handleCloseModal = () => {
        videoStreamRef.current?.stopVideo();
        onCloseModal();
    };

    const onDevicesFound = (devices: MediaDeviceInfo[], usedDevice: MediaDeviceInfo | undefined) => {
        setDevices(devices);
        setPreferredDevice(usedDevice);
    };

    const handleDeviceChange = (selectedDevice: TSelectOption) => {
        const deviceMatch = devices.find(device => device.deviceId === selectedDevice.value);

        if (deviceMatch) {
            videoStreamRef.current?.stopVideo();
            videoStreamRef.current?.startVideo(deviceMatch);
            onDeviceChange(deviceMatch.deviceId);
        }
    };

    const renderDeviceTooltip = () => {
        return (
            <div className="u-d-flex u-align-center u-gap-6">
                <span>{t("book.wtp_scanner.choose_camera")}</span>
                <Tooltip toolTipContent={t("book.wtp_scanner.video_focus_tip")}>
                    <InfoIcon color={style.brandBlueColor} />
                </Tooltip>
            </div>
        );
    };

    return (
        <Modal
            open={show}
            onClose={handleCloseModal}
            closeOnOutsideClick={false}
            size="lg"
            {...(isIframe && isDesktop && { fromTop: 120 + iframeOffsetTop + "px" })}
            {...(isIframe &&
                (isMobile || isTablet) && {
                    fromTop: 80 + iframeOffsetTop + "px",
                })}
        >
            <div className="u-pt-24">
                <div className="u-mb-18">
                    <Heading type="h2" styleAs="h6" className="u-mb-12">
                        {t("book.wtp_scanner_heading")}
                    </Heading>
                    <Notification type="info" className="u-mb-18">
                        <ul className="u-pl-18 u-mb-0">
                            <li>{<RichText content={t("book.wtp_scanner_position_tip")} />}</li>
                            <li>{t("book.wtp_scanner_brightness_tip")}</li>
                        </ul>
                    </Notification>
                    {devices.length > 1 && (
                        <Select
                            type="standard"
                            label={renderDeviceTooltip()}
                            options={devices.map(device => ({ label: device.label, value: device.deviceId }))}
                            onSelectCallback={handleDeviceChange}
                            defaultValue={preferredDevice?.deviceId}
                        />
                    )}
                </div>
                <WtpVideoStream
                    ref={videoStreamRef}
                    imageHandler={performOCR}
                    onCardNumberMatch={onMatch}
                    azureVisionLoading={azureVisionLoading}
                    onDevicesFound={onDevicesFound}
                    lastUsedDeviceId={selectedDeviceId}
                ></WtpVideoStream>
                {azureVisionLoading && <p className="u-text-center u-mt-18">{t("book.checkout.ocr_loading")}</p>}
            </div>
        </Modal>
    );
};
